'use client'

import { useEffect, useState } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { useTranslations } from '@/hooks/useTranslations'
import { createClient } from '@/utils/supabase/client'
import { getIntentWorker } from '@/lib/intent-worker'
import { drainLegacyOfflineQueue } from '@/lib/legacy-queue-drain'
import { flushDebugBuffersToServer } from '@/lib/error-buffer'
import { ConnectivityService } from '@/lib/connectivity'
import { registerBackgroundSync, unregisterBackgroundSync } from '@/lib/capacitor-background'

export function ClientBoot() {
  const t = useTranslations('sync')
  const pathname = usePathname()
  const [needsReauth, setNeedsReauth] = useState(false)

  // Reachability nudge on every route change. Without a trustworthy native
  // offline event (Android reports connected=true on the wifi→cellular
  // handover) a mid-session drop is otherwise only caught by the 10s poll —
  // probing on navigation aligns detection with the moments the user acts.
  // check() self-dedupes concurrent calls, so rapid navs cost one HEAD.
  useEffect(() => {
    void ConnectivityService.getInstance().check('nav')
  }, [pathname])

  useEffect(() => {
    const worker = getIntentWorker()
    const supabase = createClient()

    void supabase.auth.getSession().then(({ data }) => {
      if (data?.session) {
        worker.start()
        void drainLegacyOfflineQueue().catch((err) => {
          console.warn('[legacy-drain] failed', err)
        })
        void registerBackgroundSync().catch((err) => {
          console.warn('[bg-sync] register failed', err)
        })
      }
    })

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setNeedsReauth(false)
        worker.resumeAfterReauth()
        worker.start()
      }
      if (event === 'SIGNED_OUT') {
        worker.stop()
      }
    })

    const onNeedsReauth = () => setNeedsReauth(true)
    window.addEventListener('needs-reauth', onNeedsReauth)

    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel('imki-auth')
      channel.onmessage = (event) => {
        if (event.data?.type === 'needs-reauth') setNeedsReauth(true)
        if (event.data?.type === 'auth-resumed') setNeedsReauth(false)
      }
    } catch { /* older browsers: no cross-tab channel */ }

    // Auto-ship captured client errors + history.replaceState calls to
    // /api/debug/log. Watermarks in localStorage make flushes idempotent, so
    // the initial mount-time flush plus a re-flush on each online transition
    // drain the full backlog. ConnectivityService wraps the raw `online`
    // event, so we don't need a separate window listener.
    void flushDebugBuffersToServer()
    const unsubConn = ConnectivityService.getInstance().subscribe((online) => {
      if (online) void flushDebugBuffersToServer()
    })

    return () => {
      authListener?.subscription.unsubscribe()
      window.removeEventListener('needs-reauth', onNeedsReauth)
      unsubConn()
      channel?.close()
      worker.stop()
      void unregisterBackgroundSync()
    }
  }, [])

  return (
    <>
      {needsReauth && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[92%]"
          role="alert"
          aria-live="assertive"
        >
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg px-4 py-3 shadow-md">
            <p className="text-sm font-medium">{t('reauthRequired')}</p>
          </div>
        </div>
      )}
    </>
  )
}
